import Vue from 'vue';
import { toast } from '@mx/vix';
import { env } from '@/common/env';
import rootBridge from '@mx/fsp-mweb-bridge';

enum Runtime {
  Prod,
  Staging,
  Test,
  Dev,
  Local
}

class Bridge {
  setTitle(title: string) {
    document.title = title;
    KNB.ready(() => {
      KNB.setTitle({ title });
    });
  }

  // 设置返回按钮
  setLLButton(handle: () => void, content?: string) {
    let llButtonConfig = { handle } as KNBLLButtonConfig;
    if (!content) {
      llButtonConfig = { ...llButtonConfig, icon: 'H5_Back' };
    } else if (this.isBase64(content)) {
      llButtonConfig = { ...llButtonConfig, type: 'base64', icon: content };
    } else {
      llButtonConfig = { ...llButtonConfig, type: 'text', text: content };
    }
    KNB.ready(() => {
      KNB.setLLButton(llButtonConfig);
    });
  }

  // 设置右上角按钮
  setRRButton(handle: () => void, content: string) {
    let rrButtonConfig = { handle, position: 'RR' } as KNBNavButtonsConfig;
    if (this.isBase64(content)) {
      rrButtonConfig = { ...rrButtonConfig, type: 'base64', icon: content };
    } else {
      rrButtonConfig = { ...rrButtonConfig, type: 'text', text: content };
    }
    KNB.ready(() => {
      KNB.setNavButtons(rrButtonConfig);
    });
  }

  resetNavButton(position: string = 'RR') {
    KNB.ready(() => {
      KNB.setNavButtons({
        position,
        disable: 1
      });
    });
  }

  // 登录
  login(successCallback?: () => void, backUrl: string = ''): void {
    const loginTimeKey = 'payfe_login_time';
    const loginTime = Number(sessionStorage.getItem(loginTimeKey)) || 1;
    sessionStorage.setItem(loginTimeKey, `${loginTime + 1}`);

    if (loginTime > 3) {
      setTimeout(() => {
        toast.show('text', '登录状态有点问题，请稍后再试');
      }, 0);
      sessionStorage.removeItem(loginTimeKey);
      return;
    }

    if (env.isNative) {
      KNB.ready(() => {
        this.knbLogin(() => {
          successCallback && successCallback();
        });
      });
    } else {
      this.webLogin(backUrl);
    }
  }

  webLogin(backUrl?: string): void {
    let runtime = Runtime.Test;
    if (/\.dev\.sankuai\.com/.test(location.host)) {
      runtime = Runtime.Dev;
    } else if (/\.test\.(sankuai|meituan)\.com/.test(location.host)) {
      runtime = Runtime.Test;
    } else if (/\.st\.(sankuai|meituan)\.com/.test(location.host)) {
      runtime = Runtime.Staging;
    } else if (/\.meituan\.com/.test(location.host)) {
      runtime = Runtime.Prod;
    } else {
      runtime = Runtime.Local;
    }
    let loginAccountHost = 'passport.meituan.com';
    if (runtime >= 2) {
      loginAccountHost = 'passport.fe.test.sankuai.com';
    }
    let link;
    if (backUrl) {
      link = `${runtime >= 2 ? 'http:' : window.location.protocol}//${loginAccountHost}/useraccount/ilogin?backurl=${encodeURIComponent(backUrl)}`;
    } else {
      link = `${window.location.protocol}//${loginAccountHost}/useraccount/ilogin?backurl=${encodeURIComponent(location.href)}`;
    }
    location.href = link;
  }

  knbLogin(successCallback: () => void): void {
    KNB.ready(() => {
      KNB.login({
        success: () => {
          if (successCallback) {
            successCallback();
          } else {
            location.reload();
          }
        }
      });
    });
  }

  subscribe(action: string, handle: () => void) {
    KNB.ready(() => {
      KNB.subscribe({
        action,
        handle
      });
    });
  }

  unSubscribe(action: string, success: () => void) {
    KNB.ready(() => {
      KNB.unsubscribe({
        action,
        success
      });
    });
  }

  EHShow() {
    KNB.ready(() => {
      KNB.use('eh.show');
    });
  }

  navBarHidden() {
    KNB.ready(() => {
      KNB.setNavigationBarHidden({
        flag: 1
      });
    });
  }

  setStatusBarStyle(color: number) {
    KNB.ready(() => {
      KNB.setStatusBarStyle({
        style: color // 1:状态栏字体为白色, 0:状态栏字体为黑色
      });
    });
  }

  setBounces(val: number) {
    KNB.ready(() => {
      KNB.setBouncesEnabled({
        enable: val // 1表示允许反弹，0表示禁止。default: 1
      });
    });
  }

  openWebview(url: string) {
    if (KNB.env.isNative) {
      KNB.ready(() => {
        KNB.openWebview({
          url,
          qs: {
            _knbopeninapp: 1, // 有多个美团系app时，确保在当前app内打开
            noquery: 1
          }
        });
      });
    } else {
      location.href = url;
    }
  }

  closeWebview() {
    KNB.ready(() => {
      KNB.closeWebview({});
    });
  }

  // 打开新页面
  openPage(url: string, errorCallback: any, noTitleBar: 0) {
    if (env.isInWeixin) {
      location.href = url;
    } else {
      KNB.ready(() => {
        KNB.openPage({
          url,
          query: {
            _knbopeninapp: 1,
            notitlebar: noTitleBar
          },
          fail: errorCallback
        });
      });
    }
  }

  closePage(): void {
    if (env.isInWeixin) {
      (window as any).wx.closeWindow();
    } else {
      KNB.ready(() => {
        KNB.closePage();
      });
    }
  }

  // 关闭并打开新 WebView
  jumpPage(url: string) {
    KNB.ready(() => {
      KNB.jumpPage({
        url,
        query: {
          notitlebar: 0
        }
      });
    });
  }

  setStorage(storageKey: string, storageValue: string, success: (res: any) => void, fail: (error: any) => void) {
    KNB.ready(() => {
      KNB.setStorage({
        key: storageKey,
        value: storageValue,
        level: 0,
        success,
        fail
      });
    });
  }

  getStorage(storageKey: string, success: (res: any) => void) {
    KNB.ready(() => {
      KNB.getStorage({
        key: storageKey,
        success
      });
    });
  }

  clearStorage(storageKey: string) {
    KNB.ready(() => {
      KNB.clearStorage({
        key: storageKey
      });
    });
  }

  // 设置风控信息
  getFingerprint(): Promise<string> {
    return new Promise((resolve, reject) => {
      KNB.ready(() => {
        KNB.getFingerprint({
          success: (data: any) => {
            resolve(data.fingerprint);
          },
          fail() {
            reject();
          }
        });
      });
    });
  }

  // 请求开启定位服务
  openLocationService() {
    return new Promise((resolve, reject) => {
      KNB.ready(() => {
        KNB.requestPermission({
          sceneToken: 'jf-b04865d4d198ff31',
          type: 'location',
          readonly: false,
          forceJump: true,
          success: (data: any) => {
            resolve(data);
          },
          fail(data) {
            KNB.use('openAppSetting', {
              fail: () => {
                reject(data);
              }
            });
          }
        });
      });
    });
  }

  // 检查定位服务
  checkLocationService() {
    return new Promise((resolve, reject) => {
      if (env.isInWeixin) {
        resolve(null);
      } else {
        KNB.ready(() => {
          KNB.requestPermission({
            sceneToken: 'jf-b04865d4d198ff31',
            type: 'location',
            readonly: false,
            forceJump: true,
            success: (data: any) => {
              resolve(data);
            },
            fail(data) {
              reject(data);
            }
          });
        });
      }
    });
  }

  // 获取定位信息
  getLocation() {
    return new Promise((resolve, reject) => {
      if (env.isInWeixin) {
        (window as any).wx.getLocation({
          type: 'wgs84', // 默认为wgs84的gps坐标，如果要返回直接给openLocation用的火星坐标，可传入'gcj02'
          isHighAccuracy: true,
          success: (res: any) => {
            resolve({
              lat: res.latitude,
              lng: res.longitude
            });
          }
        });
      } else {
        KNB.ready(() => {
          KNB.getLocation({
            sceneToken: 'jf-b04865d4d198ff31',
            success: (data: any) => {
              // 多加一个埋点标识，辅助native排查
              const { lat, lng } = data;
              if (typeof (lat) === 'undefined' || typeof (lng) === 'undefined') {
                data.addflag = 'getLocation';
              }
              resolve(data);
            },
            fail: (data) => {
              reject(data);
            }
          });
        });
      }
    });
  }

  // 展示键盘，不支持iOS
  showKeyboard() {
    KNB.ready(() => {
      KNB.showKeyboard({
        success: () => { },
        fail: (err: any) => { }
      });
    });
  }

  // 获取登录用户信息
  getUserInfo() {
    return new Promise((resolve, reject) => {
      KNB.ready(() => {
        KNB.getUserInfo({
          success: (data: any) => {
            resolve(data);
          }
        });
      });
    });
  }

  // 获取城市信息
  getCity() {
    return new Promise((resolve, reject) => {
      KNB.ready(() => {
        KNB.getCity({
          success: (data: any) => {
            resolve(data);
          }
        });
      });
    });
  }

  // 右上角分享
  configShare(config: KNBShareConfig) {
    KNB.ready(() => {
      KNB.configShare(config);
    });
  }

  // 获取定位信息
  share(config: KNBShareConfig) {
    return new Promise<void>((resolve, reject) => {
      KNB.ready(() => {
        // @ts-ignore
        KNB.share({
          sceneToken: 'jf-b04865d4d198ff31', // 受隐私合规中长期方案影响，如果分享的是本地图片，新增sceneToken字段，权限说明参考如下表格
          //标题
          title: config.title,
          // 分享描述
          desc: config.desc,
          //分享图标，建议分享图片尺寸 100X100~120X120, 大小不超过32K
          image: config.image,
          //分享链接，分享出去时需要进行short url处理
          url: config.url,
          // 传给客户端的也是数组; 如果只提供了一个渠道，直接调起分享即可
          // 无需弹出modal窗口（单一渠道直接调起美团实现，见下方表格）
          channel: config.channel,
          success: () => {
            resolve();
          },
          //用户取消了分享执行fail函数（美团）
          fail: () => {
            reject();
          }
        });
      });
    });
  }

  // 打开相机扫码
  scanQRCode() {
    return new Promise((resolve, reject) => {
      if (env.isInWeixin) {
        (window as any).wx.scanQRCode({
          needResult: 1, // 默认为0，扫描结果由微信处理，1则直接返回扫描结果，
          scanType: ['qrCode'], // 可以指定扫二维码还是一维码，默认二者都有
          success: (res: any) => {
            resolve(res.resultStr); // 当needResult 为 1 时，扫码返回的结果
          }
        });
      } else {
        KNB.ready(() => {
          KNB.scanQRCode({
            needResult: 1, // 扫码成功之后的行为
            success: (res) => {
              resolve(res.scanResult); // 当needResult 为 1 时，扫码返回的结果
            },
            fail: (res) => {
              reject(JSON.stringify(res));
            }
          });
        });
      }
    });
  }

  async getBocDeviceInfo() {
    if (!env.isInBoc) {
      return {};
    }
    return new Promise((resolve) => {
      document.addEventListener('deviceready', () => {
        this.bocNativeBridge('getDeviceInfo', {}, (data) => {
          resolve({
            deviceNo: data?.deviceNo || '',
            system: data?.system || '',
            sysVersion: data?.sysVersion || '',
          });
        });
      });
    });
  }

  private isBase64(content: string): boolean {
    return content.indexOf('data:image') === 0;
  }
}

const initBridge = new Bridge();
export const bridge = Object.assign(initBridge, rootBridge);

// extend Vue prototype
declare module 'vue/types/vue' {
  interface Vue {
    $bridge: typeof bridge;
  }
}

Vue.prototype.$bridge = bridge;
